import React from 'react';
import { Dropdown, Tooltip } from 'antd';
import type { MenuProps } from 'antd';
import type { Document } from 'types/library';

interface Props {
    doc: Document;
    variant: 'grid' | 'list';
    onDownload: () => void;
    onDelete: () => void;
    onMove: () => void;
    onRename: () => void;
    onChangeThumbnail: () => void;
}

const TYPE_META: Record<string, { label: string; color: string; bg: string }> = {
    PDF:  { label: 'PDF',  color: '#FF4D4F', bg: 'rgba(255,77,79,0.12)' },
    DOC:  { label: 'DOC',  color: '#1677FF', bg: 'rgba(22,119,255,0.12)' },
    DOCX: { label: 'DOC',  color: '#1677FF', bg: 'rgba(22,119,255,0.12)' },
    XLS:  { label: 'XLS',  color: '#52C41A', bg: 'rgba(82,196,26,0.12)' },
    XLSX: { label: 'XLS',  color: '#52C41A', bg: 'rgba(82,196,26,0.12)' },
    PPT:  { label: 'PPT',  color: '#FA8C16', bg: 'rgba(250,140,22,0.12)' },
    PPTX: { label: 'PPT',  color: '#FA8C16', bg: 'rgba(250,140,22,0.12)' },
    MP4:  { label: 'VID',  color: '#722ED1', bg: 'rgba(114,46,209,0.12)' },
    MOV:  { label: 'VID',  color: '#722ED1', bg: 'rgba(114,46,209,0.12)' },
    PNG:  { label: 'IMG',  color: '#13C2C2', bg: 'rgba(19,194,194,0.12)' },
    JPG:  { label: 'IMG',  color: '#13C2C2', bg: 'rgba(19,194,194,0.12)' },
    JPEG: { label: 'IMG',  color: '#13C2C2', bg: 'rgba(19,194,194,0.12)' },
};
const DEFAULT_META = { label: 'FILE', color: '#8C8C8C', bg: 'rgba(140,140,140,0.12)' };

function getMeta(name: string) {
    const ext = name.split('.').pop()?.toUpperCase() ?? '';
    return TYPE_META[ext] ?? DEFAULT_META;
}

function formatSize(bytes: number): string {
    if (!bytes) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
}

const MoreBtn: React.FC<{ onClick: (e: React.MouseEvent) => void }> = ({ onClick }) => (
    <button
        className="flex h-7 w-7 items-center justify-center rounded-md border-0 bg-transparent text-text-muted opacity-0 transition-all group-hover:opacity-100 hover:bg-bg-hover hover:text-text-primary"
        onClick={onClick}
    >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
            <circle cx="12" cy="5" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="12" cy="19" r="1.8" />
        </svg>
    </button>
);

const FileCard: React.FC<Props> = ({ doc, variant, onDownload, onDelete, onMove, onRename, onChangeThumbnail }) => {
    const meta = getMeta(doc.name);

    const menu: MenuProps['items'] = [
        { key: 'download', label: 'Tải xuống', onClick: e => { e.domEvent.stopPropagation(); onDownload(); } },
        { key: 'rename', label: 'Đổi tên', onClick: e => { e.domEvent.stopPropagation(); onRename(); } },
        { key: 'move', label: 'Di chuyển', onClick: e => { e.domEvent.stopPropagation(); onMove(); } },
        { key: 'thumbnail', label: 'Cập nhật thumbnail', onClick: e => { e.domEvent.stopPropagation(); onChangeThumbnail(); } },
        { type: 'divider' },
        { key: 'delete', label: 'Xóa', danger: true, onClick: e => { e.domEvent.stopPropagation(); onDelete(); } },
    ];

    if (variant === 'list') {
        return (
            <div className="group lib-list-row lib-list-row--file" onClick={onDownload} role="button" tabIndex={0}>
                <div className="lib-list-row__icon lib-list-row__icon--file" style={{ background: meta.bg }}>
                    <span className="lib-type-chip" style={{ color: meta.color }}>{meta.label}</span>
                </div>
                <span className="lib-list-row__name">{doc.name}</span>
                <span className="lib-list-row__cell">{formatSize(doc.size)}</span>
                <span className="lib-list-row__cell">{doc.updated_at.slice(0, 10)}</span>
                <Dropdown menu={{ items: menu }} trigger={['click']} placement="bottomRight">
                    <MoreBtn onClick={e => e.stopPropagation()} />
                </Dropdown>
            </div>
        );
    }

    return (
        <div className="group lib-file-card" onClick={onDownload} role="button" tabIndex={0}>
            <div className="lib-file-card__header">
                <div className="lib-file-card__icon" style={{ background: meta.bg }}>
                    <span className="lib-type-chip" style={{ color: meta.color }}>{meta.label}</span>
                </div>
                <Tooltip title={doc.name}>
                    <span className="lib-file-card__name">{doc.name}</span>
                </Tooltip>
                <Dropdown menu={{ items: menu }} trigger={['click']} placement="bottomRight">
                    <MoreBtn onClick={e => e.stopPropagation()} />
                </Dropdown>
            </div>
            <div className="lib-file-card__preview" style={{ background: meta.bg }}>
                <span className="lib-file-card__preview-label" style={{ color: meta.color }}>{meta.label}</span>
            </div>
        </div>
    );
};

export default FileCard;
